import React, { useEffect, useMemo, useState } from "react";
import { useLanguage } from "../i18n/LanguageContext";
import { Share2, QrCode, CheckCircle, Check, Copy, Home } from "lucide-react";
import Barcode from "react-barcode";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { TicketDto, TicketsClient } from "../web-api-client";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { useToast } from "../hooks/use-toast";
import { useIsMobile } from "../hooks/use-mobile";
import MobileHeader from "../components/MobileHeader";
import LoadingIndicator from "../components/LoadingIndicator";
import ErrorRetry from "../components/ui/ErrorRetry";
import { scrollToTop } from "../utils/scrollToTop";
import { useParams } from "react-router-dom";
import { appConfig } from "../appconfig";

function Ticket() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const location = useLocation();
  const [isMobile] = useIsMobile();
  const { t } = useLanguage();

  const from = location.state?.from;
  const view = location.state?.view;

  const [isCopied, setIsCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const [ticketData, setTicketData] = useState<TicketDto | null>(null);
  const { id: routeId } = useParams();
  const viewId = routeId;

  const handleCopycode = async (code: string) => {
    try {
      navigator.clipboard.writeText(code);

      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);

      toast({
        variant: "success",
        title: t("maphieudaluu"),
        description: "",
        duration: 3000,
      });
    } catch (err) {
      console.error("Failed to copy code:", err);
    }
  };

  const handleContinue = () => {
    navigate("/");
  };

  const onBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    fetchTicket();
    scrollToTop();
  }, []);

  useEffect(() => {
    document.title = t("thongtinphieukham");
  }, [t]);

  const fetchTicket = async () => {
    setIsLoading(true);

    const client = new TicketsClient(appConfig.apiBaseUrl, {
      fetch: fetchWithTimeout(),
    });
    try {
      var data = await client.getTicket(viewId ?? "");

      setTicketData(data);
    } catch (error: any) {
      if (error.name === "AbortError") {
        setHasError(true);

        toast({
          variant: "destructive",
          title: t("maychuquahan"),
          description: "",
          duration: 3000,
        });

        console.error("Máy chủ mất quá lâu để phản hồi");
      } else if (error.response) {
        console.error(error.response);
      } else {
        // navigate("/not-found");

        return;
      }
    }

    setIsLoading(false);
  };

  const handleRetry = () => {
    setHasError(false);
    fetchTicket();
  };

  return isLoading ? (
    <LoadingIndicator />
  ) : hasError ? (
    <div className="p-4 flex justify-center pb-[15px]">
      <ErrorRetry onRetry={handleRetry} />
    </div>
  ) : (
    ticketData && (
      <div>
        {isMobile && view === "detail" && (
          <MobileHeader title={t("phieukhambenh")} onBack={onBack} />
        )}
        <div className="h-full p-3">
          <div className="max-w-md mx-auto rounded-xl shadow-xl overflow-hidden">
            {/* Header */}
            <div className="bg-green-500 text-white py-4 px-4 text-center relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-green-600 animate-pulse"></div>

              <div className="relative z-10 flex items-center justify-center space-x-3">
                <div className="relative">
                  <div className="absolute inset-0 rounded-full border-2 border-white/30 animate-ping"></div>
                  <div
                    className="absolute inset-0 rounded-full border-2 border-white/20 animate-ping"
                    style={{ animationDelay: "0.5s" }}
                  ></div>

                  <CheckCircle
                    size={28}
                    className="text-white animate-bounce relative z-10"
                    style={{ animationDuration: "2s" }}
                  />
                </div>
                <div>
                  <p className="font-bold text-lg">{t("datkhamthanhcong")}</p>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="p-4 relative bg-white">
              <p className="text-lg font-semibold mb-4 text-center">
                {t("phieudangkykham")}
              </p>
              {/* Barcode and Time Section */}
              <div className="flex flex-wrap justify-between items-center mb-6">
                <div className="flex flex-start mb-2 items-end">
                  <div>
                    <p className="text-base text-gray-600 mb-2 text-center">
                      {t("maphieu")}
                    </p>
                    <div className="flex flex-col items-start">
                      {/* <QrCode size={60} className="text-gray-600" /> */}
                      <Barcode
                        value={String(ticketData.appointmentId ?? "")}
                        width={1}
                        height={50}
                        background="#ffffff"
                        lineColor="#000000"
                      />
                    </div>
                  </div>

                  <button
                    onClick={() =>
                      handleCopycode(String(ticketData.appointmentId ?? ""))
                    }
                    className={`
                p-1.5 mb-1 ml-1 rounded-full transition-all duration-200 h-fit
                ${
                  isCopied
                    ? "bg-green-100 text-green-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-600"
                }
              `}
                    title={isCopied ? t("madacopy") : t("copymaphieu")}
                  >
                    {isCopied ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                </div>

                <div className="text-right">
                  <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2">
                    <p className="text-base text-green-600 font-medium">
                      {t("giokham")}
                    </p>
                    <p className="md:text-xl text-lg font-bold text-green-700">
                      {ticketData.appointmentTime ?? "00:00"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Details Section */}
              <div className="space-y-4">
                <div className="flex">
                  <div className="bg-green-50 h-fit py-2 whitespace-nowrap text-green-700 rounded-lg px-3 py-1 text-base font-medium mr-3">
                    {t("phongkhamlabel")}
                  </div>
                  <div className="bg-gray-100 text-gray-700 rounded-lg px-3 py-2 text-base line-clamp-2">
                    {ticketData.clinicName}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-2">
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600 text-base">{t("khu")}</span>
                    <span className="font-medium">
                      {ticketData.formattedDepartmentName}
                    </span>
                  </div>

                  {ticketData.doctorName && (
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-600 text-base">
                        {t("bacsikham")}
                      </span>
                      <span className="font-medium ">
                        {ticketData.doctorName}
                      </span>
                    </div>
                  )}

                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600 text-base">
                      {t("thoigiankham")}
                    </span>
                    <span className="font-medium">
                      {ticketData.appointmentTime} -{" "}
                      {ticketData.appointmentDate}
                    </span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600 text-base">
                      {t("benhnhan")}
                    </span>
                    <span className="font-medium">{ticketData.fullName}</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600 text-base">
                      {t("ngaysinh")}
                    </span>
                    <span className="font-medium">
                      {ticketData.dateOfBirth}
                    </span>
                  </div>

                  {/* <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="whitespace-nowrap text-gray-600 text-base">
                      Lý do khám
                    </span>
                    <span className="ml-8 font-medium line-clamp-2">
                      {ticketData.reasonForVisit ?? "Khám bệnh"}
                    </span>
                  </div> */}
                </div>
              </div>

              {/* Note Section */}
              <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-gray-700 leading-relaxed">
                  <span className="font-semibold mr-2">{t("luuy")}</span>
                  {t("comattrước15phut")}
                </p>
              </div>
            </div>

            {/* Share Button */}
            <div className="p-6 pt-0 bg-white cursor-pointer">
              <div
                onClick={handleContinue}
                className="w-full button-primary text-white font-semibold py-3 px-6 rounded-xl flex items-center justify-center space-x-2 transition-colors"
              >
                <Home size={20} />
                <span>{t("tieptuckhambenh")}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  );
}

export default Ticket;
